import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Users, Briefcase, Gauge, Loader2,
  Phone, MessageSquare, Car,
} from "lucide-react";
import BUSINESS from "../config/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** MobileRentals — /m/rentals — swipeable premium rental cards.
 *  Tapping RESERVE opens the full digital rental contract flow
 *  (BookRental) so the customer signs, uploads their ID, and
 *  submits for admin approval — no more "inquiry" shortcut. */
export default function MobileRentals() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vehicles`, { params: { available_for: "rental" } });
        if (!cancelled) setVehicles(data || []);
      } catch (e) {
        if (!cancelled) setVehicles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(vehicles.map((v) => v.category).filter(Boolean)));
    return ["All", ...cats];
  }, [vehicles]);

  const filtered = useMemo(() => {
    if (category === "All") return vehicles;
    return vehicles.filter((v) => v.category === category);
  }, [vehicles, category]);

  useEffect(() => { setActiveIdx(0); }, [category]);

  return (
    <div data-testid="mobile-rentals" className="min-h-screen bg-[#071A2F] text-white pb-28 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute -top-32 -left-16 w-80 h-80 rounded-full bg-[#39D6FF]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 -right-24 w-96 h-96 rounded-full bg-[#16B3DD]/8 blur-3xl pointer-events-none" />

      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-6">
        <button
          type="button"
          onClick={() => navigate("/m")}
          data-testid="rentals-back"
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white active:scale-95 transition"
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="text-[9px] tracking-[0.4em] text-[#39D6FF]/70">RENTAL FLEET</div>
        <div className="w-10 h-10" />
      </div>

      {/* Hero copy */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="px-6 pt-6 pb-3"
      >
        <div className="inline-flex items-center gap-1.5 mb-4 px-3 py-1 rounded-full border border-[#39D6FF]/40 bg-[#39D6FF]/10 text-[10px] tracking-[0.3em] text-[#39D6FF]">
          <Car size={11} /> DAILY · WEEKLY · MONTHLY
        </div>
        <h1 className="font-serif text-4xl leading-tight">
          Rent the <span className="text-transparent bg-clip-text bg-gradient-to-b from-[#F8FAFC] to-[#39D6FF]">extraordinary</span>.
        </h1>
        <p className="mt-3 text-sm text-white/60">
          Rideshare-ready · Personal · Business — swipe to explore.
        </p>
      </motion.section>

      {/* Category chips */}
      <div className="mt-3 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 px-6 min-w-max">
          {categories.map((c) => {
            const active = c === category;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                data-testid={`rentals-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-[10px] tracking-[0.28em] border transition ${
                  active
                    ? "bg-[#39D6FF] text-black border-[#39D6FF] font-semibold"
                    : "bg-white/5 text-white/70 border-white/10 hover:border-white/30"
                }`}
              >
                {c.toUpperCase()}
              </button>
            );
          })}
        </div>
      </div>

      {/* Deck / cards */}
      <div className="mt-6 min-h-[440px]">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-white/40 text-xs py-16">
            <Loader2 size={14} className="animate-spin" /> Loading rentals…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-white/40 text-xs py-16 px-6">
            No rentals available in this category right now. Try another filter or call us.
          </div>
        ) : (
          <SwipeDeck
            vehicles={filtered}
            activeIdx={activeIdx}
            setActiveIdx={setActiveIdx}
            onReserve={(v) => navigate(`/book/rental/${v.id}`)}
          />
        )}
      </div>

      {/* Assist row */}
      <div className="mt-4 px-6 flex gap-3">
        <a
          href={`tel:${BUSINESS.phoneTel}`}
          data-testid="rentals-call"
          className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] text-white/80 border border-white/10 bg-white/5 hover:bg-white/8 transition inline-flex items-center justify-center gap-2"
        >
          <Phone size={13} /> CONCIERGE
        </a>
        <a
          href={`sms:${BUSINESS.phoneTel}`}
          data-testid="rentals-text"
          className="flex-1 py-3 rounded-full text-center text-[11px] tracking-[0.28em] text-white/80 border border-white/10 bg-white/5 hover:bg-white/8 transition inline-flex items-center justify-center gap-2"
        >
          <MessageSquare size={13} /> TEXT US
        </a>
      </div>

    </div>
  );
}

function SwipeDeck({ vehicles, activeIdx, setActiveIdx, onReserve }) {
  const goPrev = () => setActiveIdx((i) => Math.max(0, i - 1));
  const goNext = () => setActiveIdx((i) => Math.min(vehicles.length - 1, i + 1));

  return (
    <div className="px-6">
      <div className="relative h-[480px]">
        <AnimatePresence initial={false}>
          {vehicles.map((v, i) => {
            if (i < activeIdx || i > activeIdx + 2) return null;
            const offset = i - activeIdx;
            return (
              <motion.div
                key={v.id}
                data-testid={`rental-card-${v.id}`}
                initial={{ scale: 0.92, y: 30, opacity: 0 }}
                animate={{
                  scale: offset === 0 ? 1 : 1 - offset * 0.05,
                  y: offset * 14,
                  opacity: offset === 0 ? 1 : offset === 1 ? 0.35 : 0.12,
                  zIndex: 10 - offset,
                }}
                exit={{ x: -400, opacity: 0, rotate: -6, transition: { duration: 0.28 } }}
                transition={{ type: "spring", damping: 26, stiffness: 240 }}
                drag={offset === 0 ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -100 || info.velocity.x < -600) goNext();
                  else if (info.offset.x > 100 || info.velocity.x > 600) goPrev();
                }}
                className="absolute inset-0"
                style={{ pointerEvents: offset === 0 ? "auto" : "none" }}
              >
                <VehicleCard vehicle={v} onReserve={() => onReserve(v)} />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Pager + arrows */}
      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          disabled={activeIdx === 0}
          data-testid="rentals-prev"
          className="px-4 py-2 text-[10px] tracking-[0.28em] rounded-full border border-white/10 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed hover:border-white/30 transition"
        >
          ← PREV
        </button>
        <div className="flex gap-1.5">
          {vehicles.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-6 bg-[#39D6FF]" : "w-1.5 bg-white/20"}`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={goNext}
          disabled={activeIdx >= vehicles.length - 1}
          data-testid="rentals-next"
          className="px-4 py-2 text-[10px] tracking-[0.28em] rounded-full border border-white/10 text-white/70 disabled:opacity-30 disabled:cursor-not-allowed hover:border-white/30 transition"
        >
          NEXT →
        </button>
      </div>
    </div>
  );
}

function VehicleCard({ vehicle, onReserve }) {
  const img = vehicle.image_url || (vehicle.images && vehicle.images[0]);
  const weekly = vehicle.weekly_rate;
  const monthly = vehicle.monthly_rate;

  return (
    <div
      className="w-full h-full rounded-[28px] overflow-hidden relative border"
      style={{
        borderColor: "rgba(57,214,255,0.35)",
        background: "linear-gradient(160deg, #14110c 0%, #050505 100%)",
        boxShadow: "0 30px 60px -20px rgba(57,214,255,0.28), inset 0 1px 0 rgba(248,250,252,0.15)",
      }}
    >
      {/* Media */}
      <div className="relative h-[52%] bg-[#071A2F] overflow-hidden">
        {img ? (
          <img src={img} alt={vehicle.name} className="w-full h-full object-cover" draggable={false} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">No image</div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#071A2F]/50 backdrop-blur border border-white/10 text-[9px] tracking-[0.32em] text-[#39D6FF]">
          {vehicle.category?.toUpperCase()}
        </div>
        {vehicle.rideshare_ready && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#39D6FF] text-[9px] tracking-[0.28em] font-semibold text-black">
            RIDESHARE READY
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-5 h-[48%] flex flex-col">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif text-2xl leading-tight">{vehicle.name}</h3>
          <div className="text-right">
            <div className="font-serif text-2xl text-[#39D6FF]">${Number(vehicle.daily_rate).toFixed(0)}</div>
            <div className="text-[9px] tracking-[0.28em] text-white/40">PER DAY</div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-white/70">
          <Spec icon={<Users size={12} />} label={`${vehicle.capacity} pax`} />
          <Spec icon={<Briefcase size={12} />} label={`${vehicle.luggage_capacity} bags`} />
          <Spec icon={<Gauge size={12} />} label={vehicle.daily_mileage_limit ? `${vehicle.daily_mileage_limit} mi/day` : "Unlimited"} />
        </div>

        {(weekly || monthly) && (
          <div className="mt-3 flex gap-2 text-[10px] text-white/60">
            {weekly && (
              <div className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
                <div className="text-[8px] tracking-[0.3em] text-white/40">WEEKLY</div>
                <div className="text-[#39D6FF] text-xs font-medium">${Number(weekly).toFixed(0)}</div>
              </div>
            )}
            {monthly && (
              <div className="flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5">
                <div className="text-[8px] tracking-[0.3em] text-white/40">MONTHLY</div>
                <div className="text-[#39D6FF] text-xs font-medium">${Number(monthly).toFixed(0)}</div>
              </div>
            )}
          </div>
        )}

        <div className="mt-auto pt-4">
          <button
            type="button"
            onClick={onReserve}
            data-testid={`rental-reserve-${vehicle.id}`}
            className="w-full py-3 rounded-full text-[11px] tracking-[0.3em] font-bold text-black active:scale-[0.98] transition"
            style={{
              background: "linear-gradient(180deg, #F8FAFC 0%, #39D6FF 50%, #39D6FF 100%)",
              boxShadow: "0 6px 22px -6px rgba(57,214,255,0.6), inset 0 1px 0 rgba(255,255,255,0.55)",
            }}
          >
            RESERVE
          </button>
        </div>
      </div>
    </div>
  );
}

function Spec({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white/80">
      <span className="text-[#39D6FF]">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}
